'use client'

import { useEffect, useState } from 'react'

import { useTranslation } from '@/hooks/client'

import { SoundBars } from './sound-bars'

const WaveformIcon = () => (
  <svg
    viewBox="0 0 24 24"
    className="h-5 w-5"
    fill="none"
    stroke="currentColor"
    strokeWidth="1.8"
    strokeLinecap="round"
  >
    <path d="M3 12c1.6 0 1.6-3 3.2-3s1.6 6 3.2 6 1.6-9 3.2-9 1.6 12 3.2 12 1.6-6 3.2-6" />
  </svg>
)

const ArrowRightInline = () => (
  <svg
    viewBox="0 0 16 16"
    className="h-3.5 w-3.5"
    fill="none"
    stroke="currentColor"
    strokeWidth="2"
    strokeLinecap="round"
    strokeLinejoin="round"
  >
    <path d="M3 8h10M9 4l4 4-4 4" />
  </svg>
)

export const EditorialColumn = () => {
  const { t } = useTranslation()
  const [now, setNow] = useState<Date | null>(null)

  useEffect(() => {
    setNow(new Date())
    const id = setInterval(() => setNow(new Date()), 30_000)
    return () => clearInterval(id)
  }, [])

  const timeLabel = now ? now.toLocaleTimeString(undefined, { hour: 'numeric', minute: '2-digit' }) : ''

  return (
    <div className="relative flex flex-col">
      <div className="flex items-center gap-4">
        <span className="text-navy/55 font-mono text-[12px] tracking-[0.32em] uppercase">
          {'01 / '}
          {t('contact.eyebrowGetInTouch', 'Get in touch')}
        </span>
        <SoundBars count={6} className="h-4" />
      </div>

      <h1
        className="text-navy mt-6 text-[56px] leading-[0.95] tracking-[-0.04em] sm:text-[72px] md:text-[88px] lg:text-[96px]"
        style={{ fontFamily: 'var(--font-fraunces), Georgia, serif', fontFeatureSettings: '"ss01", "ss02"' }}
      >
        {/* Trailing space keeps the h1's text/accessible name "We're all ears." — the
            two block spans render on separate lines, but without it the concatenated
            text content reads "We'reall ears". */}
        <span className="block">{t('contact.heroPrefix', "We're")} </span>
        <span className="block">
          <em className="text-lapis italic" style={{ fontWeight: 500 }}>
            {t('contact.heroEmphasis', 'all ears')}
          </em>
          <span className="text-navy">.</span>
        </span>
      </h1>

      <p className="text-navy/75 mt-7 max-w-[480px] text-[17px] leading-[1.55] tracking-[-0.01em]">
        {t(
          'contact.description',
          'Questions about a hearing aid, a review, or your own hearing journey? Drop us a line — a real person reads every message.'
        )}
      </p>

      <a
        href="mailto:info@hearingtracker.com"
        className="border-navy/10 hover:border-navy/30 hover:shadow-xs group relative mt-10 inline-flex max-w-[460px] items-center justify-between gap-6 rounded-2xl border bg-white/70 px-6 py-5 backdrop-blur-sm transition-all duration-300 hover:-translate-y-0.5 hover:bg-white"
      >
        <div className="flex items-center gap-5">
          <div className="bg-navy relative flex h-11 w-11 items-center justify-center rounded-xl text-white">
            <WaveformIcon />
          </div>
          <div className="flex flex-col">
            <span className="text-navy/55 font-mono text-[11px] tracking-[0.24em] uppercase">
              {t('contact.directLine', 'Direct line')}
            </span>
            <span className="text-navy mt-0.5 text-[17px] font-medium tracking-[-0.01em]">info@hearingtracker.com</span>
          </div>
        </div>
        <span className="border-navy/15 text-navy group-hover:bg-navy flex h-9 w-9 shrink-0 items-center justify-center rounded-full border transition-transform duration-300 group-hover:translate-x-1 group-hover:text-white">
          <ArrowRightInline />
        </span>
      </a>

      <div className="ring-navy/10 mt-5 flex w-full items-center justify-center gap-3 rounded-full bg-white/60 px-4 py-2 ring-1 backdrop-blur-sm sm:w-fit sm:justify-start">
        <span className="relative flex h-2 w-2 shrink-0">
          <span className="bg-success/60 absolute inline-flex h-full w-full animate-ping rounded-full opacity-75" />
          <span className="bg-success relative inline-flex h-2 w-2 rounded-full" />
        </span>
        <span className="text-navy/80 text-[13px] tracking-[-0.01em]">
          {t('contact.readingNow', 'Reading now')}
          {timeLabel && (
            <span className="text-navy/50">
              {' · '}
              {t('contact.localTime', 'local {{time}}', { time: timeLabel })}
            </span>
          )}
        </span>
        <span className="text-navy/25">·</span>
        <span className="text-navy/55 font-mono text-[11px] tracking-[0.18em] uppercase">
          {t('contact.replyTime', '~24h reply')}
        </span>
      </div>

      <div className="hidden md:mt-12 md:block">
        <div className="bg-navy/15 h-px w-32" />
        <p
          className="text-navy/55 mt-4 text-[20px] leading-tight italic"
          style={{ fontFamily: 'var(--font-fraunces), Georgia, serif', fontWeight: 400 }}
        >
          {t('contact.signOffLine1', 'Yours, ear to the ground —')}
          <br />
          {t('contact.signOffLine2', 'the HearingTracker team.')}
        </p>
      </div>
    </div>
  )
}
